import { useCallback, useState } from "react";

import type { Dispatch, SetStateAction } from "react";

type UseStepActions<T extends string> = {
  goToNextStep: () => void;
  goToPrevStep: () => void;
  reset: () => void;
  canGoToNextStep: boolean;
  canGoToPrevStep: boolean;
  setStep: Dispatch<SetStateAction<T>>;
};

type SetStepCallbackType<T extends string> = (step: T | ((step: T) => T)) => void;

export function useNamedSteps<const T extends string>(steps: [T, ...T[]]): [T, UseStepActions<T>] {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStep = steps[currentStepIndex]!;

  const canGoToNextStep = currentStepIndex < steps.length;
  const canGoToPrevStep = currentStepIndex > 0;

  const setStep = useCallback<SetStepCallbackType<T>>(
    (step) => {
      // Allow value to be a function so we have the same API as useState
      const newStep = typeof step === "function" ? step(currentStep) : step;
      setCurrentStepIndex(steps.indexOf(newStep));
    },
    [currentStep, steps],
  );

  const goToNextStep = useCallback(() => {
    if (canGoToNextStep) {
      setCurrentStepIndex((step) => step + 1);
    }
  }, [canGoToNextStep]);

  const goToPrevStep = useCallback(() => {
    if (canGoToPrevStep) {
      setCurrentStepIndex((step) => step - 1);
    }
  }, [canGoToPrevStep]);

  const reset = useCallback(() => {
    setCurrentStepIndex(0);
  }, []);

  return [
    currentStep,
    {
      goToNextStep,
      goToPrevStep,
      canGoToNextStep,
      canGoToPrevStep,
      setStep,
      reset,
    },
  ];
}
